<div class="glossary-container">
  <div class="glossary-subnav">
    <motn-subnav
      [items]="subnavItems"
      [activeItem]="'#' + currentView"
    ></motn-subnav>
  </div>

  <div class="glossary-sections">
    <!-- Point Configuration Section -->
    <div class="glossary-section point-configuration-section">
      <h2 class="section-title">
        Point Configuration
        <motn-info-circle
          [message]="'Protocol-specific configuration for connecting tiles to device points.'"
        ></motn-info-circle>
      </h2>

      <!-- Protocol Subnav -->
      <div class="protocol-subnav">
        <motn-subnav
          [items]="filteredProtocolSubnavItems"
          [activeItem]="'#' + currentProtocol"
        ></motn-subnav>
      </div>

      <!-- Protocol-specific content -->
      @if (currentProtocol === DeviceType.Formula) {
        <div class="protocol-content">
          <h3>Formula Identity</h3>
          <motn-sort-table
            [table$]="formulaConfigTable$"
            [paginationManagedByParent]="false"
            [autoScroll]="false"
          ></motn-sort-table>

          <h3>Available Templates</h3>
          <motn-sort-table
            [table$]="formulaTemplatesTable$"
            [paginationManagedByParent]="false"
            [autoScroll]="false"
          ></motn-sort-table>

          <h3 class="section-title">
            Bindings
            <motn-info-circle [message]="'Up to ' + FORMULA_MAX_BINDINGS + ' bindings per formula tile. Column names follow the pattern shown — replace N with the binding number (1 to ' + FORMULA_MAX_BINDINGS + ').'"></motn-info-circle>
          </h3>
          <motn-sort-table
            [table$]="formulaBindingTable$"
            [paginationManagedByParent]="false"
            [autoScroll]="false"
          ></motn-sort-table>

          <div class="download-examples">
            <h3>Example CSV for Formula Tiles</h3>
            <div class="download-buttons-container">
              <button type="button" class="white indigo small" (click)="downloadFormulaTemplateExample(null)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Custom Formula Template</span>
              </button>

              <div class="button-with-popover">
                <button type="button" class="white indigo small" (click)="formulaTemplateMenuOpen = true">
                  <i class="fa-regular fa-arrow-down-to-bracket"></i>
                  <span>Formula Template</span>
                  <i class="fa-regular fa-chevron-down dropdown-icon"></i>
                </button>
                @if (formulaTemplateMenuOpen) {
                  <moti-popover-menu
                    [optionGroup]="formulaTemplateMenuGroup"
                    [overlayPositions]="overlayPositions"
                    (clickedOption$)="onFormulaTemplateSelected($event)"
                    (clickedAnywhere$)="onFormulaTemplateMenuClosed()"
                  ></moti-popover-menu>
                }
              </div>
            </div>
          </div>
        </div>
      } @else if (currentProtocol === DeviceType.BACnet) {
        <div class="protocol-content">
          <h3>BACnet Point Configuration</h3>
          <motn-sort-table
            [table$]="bacnetPointTable$"
            [paginationManagedByParent]="false"
            [autoScroll]="false"
          ></motn-sort-table>

          <div class="download-examples">
            <h3>Example CSV for BACnet Points</h3>
            <div class="download-buttons-container">
              <button type="button" class="white indigo small" (click)="downloadNumericExample(DeviceType.BACnet)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Numeric Tiles CSV</span>
              </button>
              <button type="button" class="white indigo small" (click)="downloadToggleExample(DeviceType.BACnet)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Toggle Tiles CSV</span>
              </button>
            </div>
          </div>
        </div>
      } @else if (currentProtocol === DeviceType.Modbus && isModbusEnabled()) {
        <div class="protocol-content">
          <h3>Modbus Point Configuration</h3>
          <motn-sort-table
            [table$]="modbusPointTable$"
            [paginationManagedByParent]="false"
            [autoScroll]="false"
          ></motn-sort-table>

          <div class="download-examples">
            <h3>Example CSV for Modbus Points</h3>
            <div class="download-buttons-container">
              <button type="button" class="white indigo small" (click)="downloadNumericExample(DeviceType.Modbus)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Numeric Tiles CSV</span>
              </button>
              <button type="button" class="white indigo small" (click)="downloadToggleExample(DeviceType.Modbus)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Toggle Tiles CSV</span>
              </button>
            </div>
          </div>
        </div>
      } @else if (currentProtocol === DeviceType.LoRaWAN) {
        <div class="protocol-content">
          <h3>LoRaWAN Point Configuration</h3>
          <motn-sort-table
            [table$]="lorawanPointTable$"
            [paginationManagedByParent]="false"
            [autoScroll]="false"
          ></motn-sort-table>

          <div class="download-examples">
            <h4>Example CSV for LoRaWAN Points</h4>
            <div class="download-buttons-container">
              <button type="button" class="white indigo small" (click)="downloadNumericExample(DeviceType.LoRaWAN)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Numeric Tiles CSV</span>
              </button>
              <button type="button" class="white indigo small" (click)="downloadToggleExample(DeviceType.LoRaWAN)">
                <i class="fa-regular fa-arrow-down-to-bracket"></i>
                <span>Toggle Tiles CSV</span>
              </button>
            </div>
          </div>
        </div>
      }
    </div>

    <!-- User Input Fields Section -->
    <div class="glossary-section">
      <h2 class="section-title">
        User Input Fields
        <motn-info-circle
          [message]="'Fields where you enter custom values specific to your configuration.'"
        ></motn-info-circle>
      </h2>
      <motn-sort-table
        [table$]="identifiersTable$"
        [paginationManagedByParent]="false"
        [autoScroll]="false"
      ></motn-sort-table>
    </div>

    <!-- Writability Section -->
    @if (getVisibleSections().includes('writability')) {
      <div class="glossary-section">
        <h2 class="section-title">
          Tile Writability
          <motn-info-circle
            [message]="'Controls whether users can modify tile values through the UI.'"
          ></motn-info-circle>
        </h2>
        <motn-sort-table
          [table$]="writabilityTable$"
          [paginationManagedByParent]="false"
          [autoScroll]="false"
        ></motn-sort-table>
      </div>
    }

    <!-- Decimal Places Section -->
    @if (getVisibleSections().includes('decimal')) {
      <div class="glossary-section">
        <h2 class="section-title">
          Decimal Places
          <motn-info-circle
            [message]="'Controls the number of decimal places displayed for numeric values.'"
          ></motn-info-circle>
        </h2>
        <motn-sort-table
          [table$]="decimalTable$"
          [paginationManagedByParent]="false"
          [autoScroll]="false"
        ></motn-sort-table>
      </div>
    }

    <!-- Multiplier Section -->
    @if (getVisibleSections().includes('multiplier')) {
      <div class="glossary-section">
        <h2 class="section-title">
          Multiplier
          <motn-info-circle
            [message]="'Numeric multipliers applied to tile values before display.'"
          ></motn-info-circle>
        </h2>
        <motn-sort-table
          [table$]="multiplierTable$"
          [paginationManagedByParent]="false"
          [autoScroll]="false"
        ></motn-sort-table>
      </div>
    }

    <!-- Transformation Conversion Formula Section -->
    @if (getVisibleSections().includes('conversion')) {
      <div class="glossary-section">
        <h2 class="section-title">
          Transformation Conversion Formula
          <motn-info-circle
            [message]="'Built-in formulas for automatic unit conversions.'"
          ></motn-info-circle>
        </h2>
        <motn-sort-table
          [table$]="conversionTable$"
          [paginationManagedByParent]="false"
          [autoScroll]="false"
        ></motn-sort-table>
      </div>
    }

    <!-- Color Section -->
    @if (getVisibleSections().includes('colors')) {
      <div class="glossary-section">
        <h2 class="section-title">
          Color
          <motn-info-circle
            [message]="'Available color options for tile appearance.'"
          ></motn-info-circle>
        </h2>
        <motn-sort-table
          [table$]="colorsTable$"
          [autoScroll]="false"
          [disableUrlUpdate]="true"
        ></motn-sort-table>
      </div>
    }

    <!-- Icon Section -->
    @if (getVisibleSections().includes('icons')) {
      <div class="glossary-section">
        <h2 class="section-title">
          Icon
          <motn-info-circle
            [message]="'Available icons for tile visual identification.'"
          ></motn-info-circle>
        </h2>
        <motn-sort-table
          [table$]="iconsTable$"
          [autoScroll]="false"
          [disableUrlUpdate]="true"
        ></motn-sort-table>
      </div>
    }

    <!-- Auto-Clear Alarms Section -->
    <div class="glossary-section">
      <h2 class="section-title">
        Auto-Clear Alarms
        <motn-info-circle
          [message]="'Controls whether alarms automatically dismiss when the alarm condition is resolved.'"
        ></motn-info-circle>
      </h2>
      <motn-sort-table
        [table$]="autoClearTable$"
        [paginationManagedByParent]="false"
        [autoScroll]="false"
      ></motn-sort-table>
    </div>

    <!-- Alarm Configuration Section -->
    <div class="glossary-section">
      <h2 class="section-title">
        Alarm Configuration
        @if (currentView === TileFormat.Numeric) {
          <motn-info-circle
            [message]="'Fields that configure alarm conditions and notifications. Numeric tiles support High Critical (>, >=), Low Critical (<, <=), High Warning (>, >=), and Low Warning (<, <=) alarm types to define thresholds.'"
          ></motn-info-circle>
        } @else {
          <motn-info-circle
            [message]="'Fields that configure alarm conditions and notifications. Toggle tiles support only Critical alarm type, which triggers based on boolean values (true/false) without operators.'"
          ></motn-info-circle>
        }
      </h2>
      <motn-sort-table
        [table$]="alarmConfigTable$"
        [paginationManagedByParent]="false"
        [autoScroll]="false"
      ></motn-sort-table>
    </div>

    <!-- Tags & Facets Section -->
    <div class="glossary-section">
      <h2 class="section-title">
        Tags &amp; Facets
        <motn-info-circle
          [message]="'Optional columns for tagging tiles. Tags are free-form labels; facets are structured key-value pairs tied to predefined profiles.'"
        ></motn-info-circle>
      </h2>
      <motn-sort-table
        [table$]="tagsAndFacetsTable$"
        [paginationManagedByParent]="false"
        [autoScroll]="false"
      ></motn-sort-table>

      <div class="profiles-subsection">
        <h3>Available Profiles</h3>
        <motn-sort-table
          [table$]="profilesTable$"
          [paginationManagedByParent]="false"
          [autoScroll]="false"
        ></motn-sort-table>
      </div>
    </div>

  </div>
</div>
